import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store/index';
import { $tk } from '@/_translate';

// Containers
const DefaultContainer = () => import('@/containers/DefaultContainer');

// Views
const Dashboard = () => import('@/views/Dashboard/Dashboard');

// Views - Pages
const Page404 = () => import('@/views/coreui/pages/Page404');
const Page500 = () => import('@/views/coreui/pages/Page500');
const Login = () => import('@/views/Pages/Login');
const Register = () => import('@/views/Pages/Register');
const PasswordForgot = () => import('@/views/Pages/PasswordReset/PasswordForgot');
const PasswordReset = () => import('@/views/Pages/PasswordReset/PasswordReset');

// Views - Administration
const Users = () => import('@/views/Administration/Users/Users');
const UserCreate = () => import('@/views/Administration/Users/UserCreate');
const UserUpdate = () => import('@/views/Administration/Users/UserUpdate');
const Roles = () => import('@/views/Administration/Roles/Roles');
const RoleCreate = () => import('@/views/Administration/Roles/RoleCreate');
const RoleUpdate = () => import('@/views/Administration/Roles/RoleUpdate');
const AuditLogs = () => import('@/views/Administration/AuditLogs');
const Notifications = () => import('@/views/Administration/Notifications/Notifications');

const Translations = () => import('@/views/Administration/Translations/Translations');
const TranslationForm = () => import('@/views/Administration/Translations/TranslationForm');

const Languages = () => import('@/views/Administration/Languages/Languages');
const LanguageForm = () => import('@/views/Administration/Languages/LanguageForm');

const PDFGenerator = () => import('@/views/PDFGenerator/PDFGenerator');
const QrScanner = () => import('@/views/QrCodeScanner/QrScanner');

// GenCrud.0.stubpart (stub fragment will be generated bellow this line - do not delete this)
// GenCrud.0.Resources.stubinsert.start
const Resources = () => import('@/views/Resources/Resources');
const ResourceForm = () => import('@/views/Resources/ResourceForm');
// GenCrud.0.Resources.stubinsert.end
// GenCrud.0.UniqueIds.stubinsert.start
const UniqueIds = () => import('@/views/UniqueIds/UniqueIds');
// GenCrud.0.UniqueIds.stubinsert.end

Vue.use(Router);

const publicRoutes = [
  'Login',
  'Register',
  'PasswordForgot',
  'PasswordReset',
];

const router = new Router({
  mode: 'hash', // https://router.vuejs.org/api/#mode
  linkActiveClass: 'open active',
  scrollBehavior: () => ({ y: 0 }),
  routes: [
    {
      path: '/pages',
      redirect: '/pages/404',
      name: null,
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [
        {
          path: '404',
          name: 'Page404',
          meta: { label: $tk('route.error404') },
          component: Page404
        },
        {
          path: '500',
          name: 'Page500',
          meta: { label: $tk('route.error500') },
          component: Page500
        },
      ]
    },
    {
      path: '/',
      redirect: { name: 'Dashboard' },
      name: null,
      component: DefaultContainer,
      children: [
        // GenCrud.1.stubpart (stub fragment will be generated bellow this line - do not delete this)
        // GenCrud.1.Resources.stubinsert.start

        {
          path: 'r',
          name: null,
          meta: { label: $tk('route.resources_list_title') },
          redirect: { name: 'ResourceUpdateByUID' },

          component: {
            render(c) { return c('router-view'); }
          },

          children: [
            {
              path: ':uid',
              name: 'ResourceUpdateByUID',
              meta: { label: $tk('route.resource_update_title') },
              props: (route) => {
                return {
                  uid: String(route.params.uid)
                };
              },
              component: ResourceForm
            },
          ]
        },

        {
          path: 'qr_scanner',
          name: null,
          meta: { label: $tk('route.qr_scanners_list_title') },
          redirect: { name: 'QrScanners' },

          component: {
            render(c) { return c('router-view'); }
          },

          children: [
            {
              path: '',
              name: 'QrScanners',
              meta: { label: $tk('route.qr_scanner_list') },
              component: QrScanner,
            }
          ]
        },

        {
          path: 'resources',
          name: null,
          meta: { label: $tk('route.resources_list_title') },
          redirect: { name: 'Resources', params: { page: 1 } },

          component: {
            render(c) { return c('router-view'); }
          },

          children: [
            {
              path: 'list/:page',
              name: 'Resources',
              meta: { label: $tk('route.resource_list') },
              props: true,
              component: Resources,
            },
            {
              path: 'create',
              name: 'ResourceCreate',
              meta: { label: $tk('route.resource_create_title') },
              component: ResourceForm
            },
            {
              path: 'update/:id',
              name: 'ResourceUpdate',
              meta: { label: $tk('route.resource_update_title') },
              props: (route) => {
                return {
                  id: Number(route.params.id)
                };
              },
              component: ResourceForm
            },
          ]
        },
        // GenCrud.1.Resources.stubinsert.end
        // GenCrud.1.UniqueIds.stubinsert.start
        {
          path: 'unique_ids',
          name: null,
          meta: { label: $tk('route.unique_ids_list_title') },
          redirect: { name: 'UniqueIds', params: { page: 1 } },

          component: {
            render(c) { return c('router-view'); }
          },

          children: [
            {
              path: 'list/:page',
              name: 'UniqueIds',
              meta: { label: $tk('route.unique_id_list') },
              props: true,
              component: UniqueIds,
            },
          ]
        },
        // GenCrud.1.UniqueIds.stubinsert.end

        {
          path: 'dashboard',
          name: 'Dashboard',
          meta: { label: $tk('route.dashboard') },
          component: Dashboard
        },
        {
          path: '/pdf_generator',
          name: null,
          meta: { label: $tk('route.pdf_generator_list_title') },
          redirect: { name: 'PDFGenerator', params: { page: 1 } },

          component: {
            render(c) { return c('router-view'); }
          },

          children: [
            {
              path: 'list/:page',
              name: 'PDFGenerator',
              meta: { label: $tk('route.qr_generator_list') },
              props: true,
              component: PDFGenerator,
            }
          ]
        },
        // ------- Administration----------------

        {
          path: '/administration',
          name: 'Administration',
          meta: { label: $tk('route.administration') },
          redirect: { name: 'Users', params: { page: 1 } },
          component: {
            render(c) { return c('router-view'); }
          },
          children: [
            {
              path: 'users',
              name: null,
              meta: { label: $tk('route.users_list_title') },
              redirect: { name: 'Users', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page?',
                  name: 'Users',
                  meta: { label: $tk('route.users_list') },
                  props: true,
                  component: Users,
                },
                {
                  path: 'create',
                  name: 'UserCreate',
                  meta: { label: $tk('route.user_create_title') },
                  component: UserCreate
                },
                {
                  path: 'update/:id',
                  name: 'UserUpdate',
                  meta: { label: $tk('route.user_update_title') },
                  props: true,
                  component: UserUpdate
                },
              ]
            },
            {
              path: 'roles',
              name: null,
              meta: { label: $tk('route.roles_list_title') },
              redirect: { name: 'Roles', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'Roles',
                  meta: { label: $tk('route.role_list') },
                  props: true,
                  component: Roles,
                },
                {
                  path: 'create',
                  name: 'RoleCreate',
                  meta: { label: $tk('route.roles_create_title') },
                  component: RoleCreate
                },
                {
                  path: 'update/:id',
                  name: 'RoleUpdate',
                  meta: { label: $tk('route.role_update_title') },
                  props: true,
                  component: RoleUpdate
                },
              ]
            },
            {
              path: 'audit-logs',
              name: null,
              meta: { label: $tk('route.audit_logs_list_title') },
              redirect: { name: 'AuditLogs', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'AuditLogs',
                  meta: { label: $tk('route.auditlogs_list') },
                  props: true,
                  component: AuditLogs,
                },
              ]
            },

            {
              path: 'translations',
              name: null,
              meta: { label: $tk('route.translations_list_title') },
              redirect: { name: 'Translations', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'Translations',
                  meta: { label: $tk('route.translations_list') },
                  props: true,
                  component: Translations,
                },
                {
                  path: 'create',
                  name: 'TranslationCreate',
                  meta: { label: $tk('route.translation_create_title') },
                  component: TranslationForm
                },
                {
                  path: 'update/:id',
                  name: 'TranslationUpdate',
                  meta: { label: $tk('route.translation_update_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: TranslationForm
                },
              ]
            },

            {
              path: 'languages',
              name: null,
              meta: { label: $tk('route.languages_list_title') },
              redirect: { name: 'Languages', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'Languages',
                  meta: { label: $tk('route.languages_list') },
                  props: true,
                  component: Languages,
                },
                {
                  path: 'create',
                  name: 'LanguageCreate',
                  meta: { label: $tk('route.language_create_title') },
                  component: LanguageForm
                },
                {
                  path: 'update/:id',
                  name: 'LanguageUpdate',
                  meta: { label: $tk('route.language_update_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: LanguageForm
                },
              ]
            },

            {
              path: 'notifications',
              name: null,
              meta: { label: $tk('route.notifications_list_title') },
              redirect: { name: 'Notifications', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'Notifications',
                  meta: { label: $tk('route.notifications_list') },
                  props: true,
                  component: Notifications,
                },
              ]
            },
          ]
        },
      ]
    },
    {
      path: '/login',
      name: 'Login',
      meta: { label: $tk('route.login') },
      component: Login
    },
    {
      path: '/register',
      name: 'Register',
      meta: { label: $tk('route.register') },
      component: Register
    },
    {
      path: '/password-forgot',
      name: 'PasswordForgot',
      meta: { label: $tk('route.forgot_password') },
      component: PasswordForgot
    },
    {
      path: '/password-reset/:token',
      props: true,
      name: 'PasswordReset',
      meta: { label: $tk('route.password_reset') },
      component: PasswordReset
    },
  ]
});

router.beforeEach((to, from, next) => {
  if (!publicRoutes.includes(to.name) && !store.getters['Auth/isLoggedIn']) {
    next({ name: 'Login' });
  }
  if (to.name === 'Login' && from.name && from.name !== 'Login'
    && from.name !== 'Register'
    && from.name !== 'PasswordForgot'
    && from.name !== 'PasswordReset') {
    sessionStorage.setItem('redirectPath', from.path);
  }

  next();
});

export default router;
